import {Banner} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect} from 'react'
import {announce} from '@github-ui/aria-live'
import type {CreateAPITaskParams, CreateAPITaskMutationParams} from '../types/api-task'
import {truncateLines} from '../utils/truncate-lines'
import styles from './TaskCreationFailedBanner.module.css'
import {TaskStartupTraceLink} from './TaskStartupTraceLink'

export const CUSTOMER_SELECTION_REQUIRED_ERROR = 'customer selection required'
export const COPILOT_SETTINGS_PATH = '/settings/copilot/features'
export const CUSTOMER_SELECTION_REQUIRED_DESCRIPTION =
  'Before you can use Copilot cloud agent, you need to pick a "Usage billed to" option in your Copilot settings.'

function isCustomerSelectionRequiredError(errorMessage?: string): boolean {
  return errorMessage?.toLowerCase().includes(CUSTOMER_SELECTION_REQUIRED_ERROR) ?? false
}

/**
 * Represents a failed task creation attempt using the new task API that can be retried.
 * Extends CreateAPITaskMutationParams so that all the original task creation parameters
 * (including clientGeneratedId) are preserved from the onError callback.
 */
export type FailedAPITaskCreationAttempt = CreateAPITaskMutationParams & {
  errorMessage: string
  timestamp: string
}

export type UIFailedTaskCreationAttempt = Pick<
  FailedAPITaskCreationAttempt,
  'problemStatement' | 'errorMessage' | 'timestamp'
> & {clientGeneratedId?: string}

/**
 * Extracts task creation parameters from a failed API task attempt,
 * removing the error-specific fields (errorMessage, timestamp) and clientGeneratedId
 * so that a new ID will be generated for the retry.
 */
export function getAPITaskParamsFromAttempt(attempt: FailedAPITaskCreationAttempt): CreateAPITaskParams {
  const {errorMessage, timestamp, clientGeneratedId, ...taskParams} = attempt
  return taskParams
}

interface TaskCreationFailedBannerProps {
  problemStatement?: string
  errorMessage?: string
  clientGeneratedId?: string
  handleRetry?: () => void
  handleDismiss: () => void
}

export function TaskCreationFailedBanner({
  problemStatement,
  errorMessage,
  clientGeneratedId,
  handleRetry,
  handleDismiss,
}: TaskCreationFailedBannerProps) {
  const isCustomerSelection = isCustomerSelectionRequiredError(errorMessage)

  const truncatedProblemStatement = truncateLines(problemStatement ?? '', 20, 1)
  const baseMessage = `Unable to start task for '${truncatedProblemStatement}'`
  const description = isCustomerSelection
    ? CUSTOMER_SELECTION_REQUIRED_DESCRIPTION
    : errorMessage
      ? `${baseMessage}: ${errorMessage}`
      : `${baseMessage}. Please try again.`

  // The banner is inserted after an async submission and its title is hidden,
  // so screen-reader users on the submit control would otherwise not learn the
  // task failed. Announce assertively for this blocking error (WCAG 4.1.3).
  useEffect(() => {
    announce(`Task creation failed. ${description}`, {assertive: true})
  }, [description])

  const descriptionWithTrace = (
    <>
      {description}
      <TaskStartupTraceLink diagnosticId={clientGeneratedId} />
    </>
  )

  if (isCustomerSelection) {
    return (
      <Banner
        variant="critical"
        title="Task creation failed"
        hideTitle
        description={descriptionWithTrace}
        primaryAction={
          <Banner.PrimaryAction as="a" href={COPILOT_SETTINGS_PATH}>
            Settings
          </Banner.PrimaryAction>
        }
        secondaryAction={<Banner.SecondaryAction onClick={handleDismiss}>Dismiss</Banner.SecondaryAction>}
      />
    )
  }

  return (
    <Banner
      variant="critical"
      title="Task creation failed"
      hideTitle
      description={descriptionWithTrace}
      secondaryAction={<Banner.SecondaryAction onClick={handleDismiss}>Dismiss</Banner.SecondaryAction>}
      // Retry is only offered when the caller supplies a handler. Non-idempotent
      // flows (e.g. environment-task creation) omit it to avoid duplicate tasks.
      primaryAction={handleRetry ? <Banner.PrimaryAction onClick={handleRetry}>Retry</Banner.PrimaryAction> : undefined}
    />
  )
}

interface TaskCreationFailedBannerListProps {
  attempts: UIFailedTaskCreationAttempt[]
  onRetry?: (index: number) => void
  onDismiss: (index: number) => void
  className?: string
}

export function TaskCreationFailedBannerList({
  attempts,
  onRetry,
  onDismiss,
  className,
}: TaskCreationFailedBannerListProps) {
  if (attempts.length === 0) {
    return null
  }

  return (
    <div className={clsx(styles.bannerList, className)}>
      {attempts.map((attempt, index) => (
        <TaskCreationFailedBanner
          key={`${attempt.problemStatement}-${attempt.timestamp}`}
          problemStatement={attempt.problemStatement}
          errorMessage={attempt.errorMessage}
          clientGeneratedId={attempt.clientGeneratedId}
          handleRetry={onRetry ? () => onRetry(index) : undefined}
          handleDismiss={() => onDismiss(index)}
        />
      ))}
    </div>
  )
}
